import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import maplibregl, { GeoJSONSource, Map as MapLibreMap, MapLayerMouseEvent, MapLayerTouchEvent, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { PARKED_COLOR, shortLabel, statusColor } from './status';
import type { Driver, Position } from './types';

// Free vector tiles of OpenStreetMap data, no API key: https://openfreemap.org
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';
const SOURCE = 'drivers';
const PIN_LAYER = 'driver-pins';
// A press that travels less than this many pixels is a click, not a drag.
const DRAG_THRESHOLD = 4;

export type MapPin = { driver: Driver, parked: boolean };

export type MapHandle = {
    flyTo: (position: Position, minZoom?: number) => void,
    fit: (positions: Position[]) => void,
};

type Props = {
    pins: MapPin[],
    selected?: string,
    // A looked-up place the list is sorted around.
    reference?: Position,
    // Position of a driver being added, not saved yet.
    draft?: Position,
    placing: boolean,
    onSelect: (truckNumber: string | undefined) => void,
    onMove: (truckNumber: string, position: Position) => void,
    onPlace: (position: Position) => void,
};

const featuresOf = (pins: MapPin[], selected: string | undefined, dragged?: { id: string, position: Position }) => ({
    type: 'FeatureCollection' as const,
    features: pins.map(({ driver, parked }) => {
        const position = dragged?.id === driver.truckNumber ? dragged.position : driver.position;
        return {
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [position.lng, position.lat] },
            properties: {
                id: driver.truckNumber,
                short: shortLabel(driver.truckNumber),
                label: driver.truckNumber,
                color: parked ? PARKED_COLOR : statusColor(driver),
                selected: driver.truckNumber === selected,
            },
        };
    }),
});

export default forwardRef<MapHandle, Props>(function MapView(props, ref) {
    const container = useRef<HTMLDivElement>(null);
    const map = useRef<MapLibreMap>();
    const ready = useRef(false);
    const latest = useRef(props);
    latest.current = props;
    const referenceMarker = useRef<Marker>();
    const draftMarker = useRef<Marker>();

    const render = (dragged?: { id: string, position: Position }) => {
        if (ready.current) {
            const { pins, selected } = latest.current;
            (map.current!.getSource(SOURCE) as GeoJSONSource).setData(featuresOf(pins, selected, dragged));
        }
    };

    useImperativeHandle(ref, () => ({
        flyTo: (position, minZoom = 9) => {
            const zoom = Math.max(map.current!.getZoom(), minZoom);
            map.current!.flyTo({ center: [position.lng, position.lat], zoom, duration: 800 });
        },
        fit: (positions) => {
            if (positions.length) {
                const bounds = new maplibregl.LngLatBounds();
                positions.forEach((position) => bounds.extend([position.lng, position.lat]));
                map.current!.fitBounds(bounds, { padding: 80, maxZoom: 9, duration: 0 });
            }
        },
    }));

    useEffect(() => {
        const instance = new maplibregl.Map({
            container: container.current!,
            style: MAP_STYLE,
            center: [-96, 38.5],
            zoom: 3.6,
            // GeoNames (CC BY 4.0) is the source of the postal code table in public/geo.
            attributionControl: { compact: true, customAttribution: 'Places: <a href="https://www.geonames.org" target="_blank">GeoNames</a>' },
            dragRotate: false,
            pitchWithRotate: false,
        });
        map.current = instance;
        instance.touchZoomRotate.disableRotation();
        instance.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');

        instance.on('load', () => {
            instance.addSource(SOURCE, { type: 'geojson', data: featuresOf([], undefined) });
            instance.addLayer({
                id: PIN_LAYER,
                type: 'circle',
                source: SOURCE,
                paint: {
                    'circle-color': ['get', 'color'],
                    'circle-radius': ['case', ['get', 'selected'], 10, 7],
                    'circle-stroke-color': ['case', ['get', 'selected'], '#0f172a', '#ffffff'],
                    'circle-stroke-width': ['case', ['get', 'selected'], 3, 2],
                },
                layout: { 'circle-sort-key': ['case', ['get', 'selected'], 1, 0] },
            });
            // Labels that would overlap are dropped, the selected driver's first in line; the pins always stay.
            instance.addLayer({
                id: 'driver-labels',
                type: 'symbol',
                source: SOURCE,
                layout: {
                    'text-field': ['step', ['zoom'], ['get', 'short'], 7, ['get', 'label']],
                    'text-font': ['Noto Sans Bold'],
                    'text-size': 12,
                    'text-anchor': 'top',
                    'text-offset': [0, 0.9],
                    'text-max-width': 12,
                    'symbol-sort-key': ['case', ['get', 'selected'], 0, 1],
                },
                paint: { 'text-color': '#0f172a', 'text-halo-color': '#ffffff', 'text-halo-width': 1.6 },
            });
            ready.current = true;
            render();
        });

        // Pins are dragged by hand: grab one, move it, and the new position is saved on release.
        const startDrag = (event: MapLayerMouseEvent | MapLayerTouchEvent) => {
            if (latest.current.placing || ('touches' in event.originalEvent && event.originalEvent.touches.length > 1)) {
                return;
            }
            event.preventDefault();
            const id: string = event.features![0].properties.id;
            const start = event.point;
            let position: Position | undefined;
            const onMove = (move: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
                if (!position && start.dist(move.point) < DRAG_THRESHOLD) {
                    return;
                }
                position = { lat: move.lngLat.lat, lng: move.lngLat.lng };
                instance.getCanvas().style.cursor = 'grabbing';
                render({ id, position });
            };
            const onUp = () => {
                instance.off('mousemove', onMove);
                instance.off('touchmove', onMove);
                instance.getCanvas().style.cursor = '';
                if (position) {
                    latest.current.onMove(id, position);
                } else {
                    latest.current.onSelect(id);
                }
            };
            instance.on('mousemove', onMove);
            instance.on('touchmove', onMove);
            instance.once('mouseup', onUp);
            instance.once('touchend', onUp);
        };
        instance.on('mousedown', PIN_LAYER, startDrag);
        instance.on('touchstart', PIN_LAYER, startDrag);
        instance.on('mouseenter', PIN_LAYER, () => { instance.getCanvas().style.cursor = latest.current.placing ? 'crosshair' : 'grab'; });
        instance.on('mouseleave', PIN_LAYER, () => { instance.getCanvas().style.cursor = latest.current.placing ? 'crosshair' : ''; });

        instance.on('click', (event) => {
            if (latest.current.placing) {
                latest.current.onPlace({ lat: event.lngLat.lat, lng: event.lngLat.lng });
            } else if (!instance.queryRenderedFeatures(event.point, { layers: [PIN_LAYER] }).length) {
                latest.current.onSelect(undefined);
            }
        });

        return () => {
            ready.current = false;
            instance.remove();
        };
    }, []);

    useEffect(() => render(), [props.pins, props.selected]);

    useEffect(() => {
        map.current!.getCanvas().style.cursor = props.placing ? 'crosshair' : '';
    }, [props.placing]);

    const syncMarker = (marker: React.MutableRefObject<Marker | undefined>, position: Position | undefined, className: string) => {
        marker.current?.remove();
        marker.current = undefined;
        if (position) {
            const element = document.createElement('div');
            element.className = className;
            marker.current = new maplibregl.Marker({ element }).setLngLat([position.lng, position.lat]).addTo(map.current!);
        }
    };
    useEffect(() => syncMarker(referenceMarker, props.reference, 'reference-marker'), [props.reference]);
    useEffect(() => syncMarker(draftMarker, props.draft, 'draft-marker'), [props.draft]);

    return <div className='map' ref={container} />;
});
